import { ComponentType } from 'react';
import { useValidateUserAccess } from '../utils/permissions-helper';
import { PageConfigItem } from './pages-config';
import { PermissionError, TierError } from '../utils/custom-errors';
import { useUserSettings } from '../utils/use-user-settings';
import SharedErrorBoundary from './shared-error-boundary';
import { enterpriseUpsellPages } from './enterprise-upsell-page/enterprise-pages-with-landing-page-enum';
import { isFeatureEnabled } from '../utils/feature-flags';
import EnterpriseFallbackPages from './enterprise-upsell-page/enterprise-fallback-pages';
import NotFoundPage from './not-found-page';
import Alert from '@mui/material/Alert';

interface ProtectedRouteProps {
  component: ComponentType;
  pageConfigItem: PageConfigItem;
}

export default function ProtectedRoute({
  component: Component,
  pageConfigItem,
}: ProtectedRouteProps) {
  const roles = useUserSettings();
  const { features } = useUserSettings();
  const comparisonRoles = roles[pageConfigItem.roleGroup];
  const featureEnabled = isFeatureEnabled(
    'enterpriseFeatureUpsellUiReact',
    features
  );
  const upsellPage = enterpriseUpsellPages.find(
    (page) => page.key === pageConfigItem.name
  );

  const fallBackPage =
    featureEnabled && upsellPage ? (
      <EnterpriseFallbackPages
        key={upsellPage.key}
        initialPage={upsellPage.key}
      />
    ) : undefined;
  const requiredFeatureFlag = pageConfigItem.requiredFeatureFlag;
  const featureFlagDisabled =
    requiredFeatureFlag !== undefined &&
    !isFeatureEnabled(requiredFeatureFlag, features);

  // useValidateUserAccess is a hook, so it has to run on every render. The flag
  // decision happens after it, before either the page or a tier/permission error.
  let accessError: unknown;
  try {
    useValidateUserAccess(pageConfigItem, comparisonRoles);
  } catch (error) {
    accessError = error;
  }

  if (featureFlagDisabled) {
    return <NotFoundPage />;
  }

  if (accessError instanceof TierError) {
    return (
      fallBackPage || <Alert severity="error">{accessError.message}</Alert>
    );
  } else if (accessError instanceof PermissionError) {
    return <Alert severity="error">{accessError.message}</Alert>;
  } else if (accessError) {
    return <Alert severity="error">An error occurred</Alert>;
  }

  return (
    <SharedErrorBoundary section={pageConfigItem.name}>
      <Component />
    </SharedErrorBoundary>
  );
}
